/**
 * SAST day helpers for the browser.
 *
 * The database runs in UTC; the business runs in SAST (Africa/Johannesburg,
 * UTC+2, no DST). Server-side this is `public.sast_date(timestamptz)`
 * (migration 20260803054500). This is the client-side counterpart, so a
 * "today" boundary means the same thing on both sides.
 *
 * Getting this wrong is not cosmetic: a payment taken at 01:30 SAST on the 1st
 * is 23:30 UTC on the last of the previous month, and would land in the wrong
 * revenue month and the wrong commission month.
 */

const SAST_DATE_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Africa/Johannesburg',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Today's date in SAST as `YYYY-MM-DD`.
 *
 * en-CA is used because it yields ISO-ordered output directly, which makes the
 * value safe to compare as a string and stable as a cache key.
 */
export function sastDateKey(at: Date = new Date()): string {
  return SAST_DATE_FMT.format(at);
}

/** Yesterday's SAST date as `YYYY-MM-DD`. */
export function sastYesterdayKey(at: Date = new Date()): string {
  return sastDateKey(new Date(at.getTime() - 86_400_000));
}

/**
 * First day of the SAST month as `YYYY-MM-DD` — the client-side counterpart of
 * the database's `(date_trunc('month', (sast_date(ts))::timestamp))::date`.
 *
 * Use this for any month filter sent to a commission or reporting view. The
 * previous approach built the key from `Date.UTC(...)`, which put a payment
 * taken between 22:00 and 23:59 UTC on a month's last day into the wrong month.
 */
export function sastMonthKey(at: Date = new Date()): string {
  return `${sastDateKey(at).slice(0, 7)}-01`;
}

/**
 * The last `count` SAST months, newest first, as `{ label, value }`.
 *
 * `value` is a `YYYY-MM-DD` first-of-month key matching `sastMonthKey`. The
 * arithmetic is deliberately pure calendar maths on the SAST-derived year and
 * month — no timezone is involved once the current month has been resolved, so
 * stepping back over a month boundary cannot drift.
 */
export function sastRecentMonths(count = 12, at: Date = new Date()): Array<{ label: string; value: string }> {
  const key = sastMonthKey(at);            // YYYY-MM-01
  const year = Number(key.slice(0, 4));
  const month = Number(key.slice(5, 7));
  const out: Array<{ label: string; value: string }> = [];
  for (let i = 0; i < count; i += 1) {
    const d = new Date(Date.UTC(year, month - 1 - i, 1));
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    out.push({
      label: d.toLocaleDateString('en-ZA', { month: 'short', year: 'numeric', timeZone: 'UTC' }),
      value: `${y}-${m}-01`,
    });
  }
  return out;
}

/** True when an ISO timestamp falls on the given SAST day (default: today). */
export function isSameSastDay(iso: string | null | undefined, day = sastDateKey()): boolean {
  if (!iso) return false;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return false;
  return sastDateKey(t) === day;
}

/**
 * Whole SAST calendar days from today to `iso`. Negative = already past,
 * `0` = today, `null` = no date or an unparseable one.
 *
 * THE canonical `daysUntil`. `portal/eslint.config.js` has banned that name in
 * page files since the shared-component doctrine landed, on the grounds that
 * "this helper already exists" — but it did not exist anywhere, so the rule
 * banned a name with nothing to import and three private copies grew anyway,
 * all disagreeing:
 *
 *   MyServiceOrderDetailPage  Math.max(0, ceil(ms/86400e3))  never negative, rounds up
 *   salesService              floor from local midnight       calendar-ish, local zone
 *   AdminInvoicesPage         floor from now                  wall-clock, not calendar
 *
 * For a deadline 34 hours away those return 2, 2 and 1. "Expires in N days" has
 * to mean one thing, so it is defined here once: a difference of calendar days
 * in SAST, which is what a person means and what §4.14 requires of any date
 * bucket in this product.
 */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  // Compare SAST calendar dates, not instants: parsing the two YYYY-MM-DD keys
  // back as UTC midnights makes the subtraction a pure day count, immune to the
  // viewer's own timezone.
  const target = Date.parse(`${sastDateKey(t)}T00:00:00Z`);
  const today = Date.parse(`${sastDateKey()}T00:00:00Z`);
  return Math.round((target - today) / 86_400_000);
}
